import { createSignal, type JSX, Show } from 'solid-js';

/**
 * Up-to-two-letter monogram from a login/handle, upper-cased. Splits on the
 * usual handle separators (`ada-lovelace` → `AL`); a single token falls back to
 * its first two letters (`octocat` → `OC`). Empty → `?`.
 */
export function initials(login: string): string {
  const s = login.trim();
  if (!s) return '?';
  const parts = s.split(/[\s._-]+/).filter(Boolean);
  const mono = parts.length > 1 ? `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}` : s.slice(0, 2);
  return mono.toUpperCase();
}

// Member avatar: the GitHub image when we have one, an initials monogram when we
// don't — or when the image URL 404s (onError flips to the fallback). Purely
// decorative: the adjacent login/email carries the accessible identity, so the
// <img> is alt="" and the whole span is aria-hidden.
//
// The monogram is painted from `data-initials` by CSS (`.avatar__mono::before`,
// `_members.scss`), not written as a text node. aria-hidden does not take text out
// of what an accessibility checker reads as a control's VISIBLE label: axe-core's
// `label-content-name-mismatch` (from 4.14) counts every on-screen text node, so a
// monogram inside a button (the sidebar's account trigger) read as a word ("DA")
// missing from the button's name. Generated content is not a text node.
export function Avatar(props: {
  readonly login: string;
  readonly avatar?: string | null;
  readonly class?: string;
}): JSX.Element {
  const [broken, setBroken] = createSignal(false);
  const showImg = (): boolean => !!props.avatar && !broken();
  return (
    <span class={`avatar${props.class ? ` ${props.class}` : ''}`} aria-hidden="true">
      <Show
        when={showImg()}
        fallback={<span class="avatar__mono" data-initials={initials(props.login)} />}
      >
        <img
          class="avatar__img"
          src={props.avatar as string}
          alt=""
          loading="lazy"
          onError={() => setBroken(true)}
        />
      </Show>
    </span>
  );
}
